import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { MeApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { getCurrentDate } from 'src/utils/dateTime';
import { StatsData } from 'src/api/me/types';

const currentDate = getCurrentDate();

export const useActivitiesStats = (initialInterval: number) => {
  const languagePairId = getLanguagePairId();
  const [intervalLength, setIntervalLength] = useState<number>(initialInterval);

  const startDate = useMemo(() => subDays(currentDate, intervalLength - 1), [intervalLength]);

  const { data: currentStats } = useQuery({
    queryKey: ['stats', languagePairId, 'daysToCompare', intervalLength],
    queryFn: () => MeApi.getStats(languagePairId as number, { daysToCompare: intervalLength }),
    enabled: !!languagePairId,
  });

  const statsDifference = useMemo(() => {
    if (!currentStats) return undefined;

    const diff: Record<string, number> = {};
    const fields: (keyof StatsData)[] = [
      'wordsHeard',
      'videosWatched',
      'videosUnderstood',
      'videosPartlyUnderstood',
      'wordsUsed',
      'membotChats',
      'wordsLearned',
      'wordsReviewed',
    ];

    fields.forEach(field => {
      const currentValue = currentStats.currentData[field];
      const beforeValue = currentStats.previousData[field];
      if (currentValue != null && beforeValue != null) {
        diff[field] = currentValue - beforeValue;
      }
    });

    const hasAny = Object.keys(diff).length > 0;
    return hasAny ? diff : undefined;
  }, [currentStats]);

  return {
    intervalLength,
    setIntervalLength,
    intervalStartDate: startDate,
    intervalEndDate: currentDate,
    stats: currentStats,
    statsDifference,
  };
};
